<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-150"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="s.open"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
        @click.self="cancel"
        @keydown.esc.stop="cancel"
      >
        <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
          <div class="px-5 pt-5 pb-3 flex items-start gap-3">
            <div
              class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              :class="iconBg"
            >
              <svg class="w-5 h-5" :class="iconColor" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="iconPath"/>
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-gray-800">{{ s.title }}</p>
              <p class="text-sm text-gray-600 mt-1 whitespace-pre-line">{{ s.message }}</p>
              <p v-if="s.detail" class="text-xs text-gray-400 italic mt-1">{{ s.detail }}</p>
            </div>
          </div>
          <div class="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
            <button
              v-if="!s.alertOnly"
              ref="cancelBtn"
              type="button"
              class="px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-200 rounded-lg"
              @click="cancel"
            >
              {{ s.cancelLabel }}
            </button>
            <button
              ref="confirmBtn"
              type="button"
              class="px-4 py-2 text-xs font-semibold rounded-lg text-white"
              :class="confirmBtnClass"
              @click="okay"
            >
              {{ s.confirmLabel }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'

const { _state: s } = useConfirm()

const cancelBtn  = ref<HTMLButtonElement | null>(null)
const confirmBtn = ref<HTMLButtonElement | null>(null)

const iconBg = computed(() => {
  if (s.tone === 'danger')  return 'bg-red-100'
  if (s.tone === 'warning') return 'bg-amber-100'
  return 'bg-navy-100'
})
const iconColor = computed(() => {
  if (s.tone === 'danger')  return 'text-red-600'
  if (s.tone === 'warning') return 'text-amber-600'
  return 'text-navy-600'
})
const iconPath = computed(() => {
  if (s.tone === 'danger') {
    // trash
    return 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3'
  }
  if (s.tone === 'warning') {
    // exclamation triangle
    return 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
  }
  // info
  return 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
})
const confirmBtnClass = computed(() => {
  if (s.tone === 'danger')  return 'bg-red-600 hover:bg-red-700'
  if (s.tone === 'warning') return 'bg-amber-600 hover:bg-amber-700'
  return 'bg-navy-600 hover:bg-navy-700'
})

function okay() {
  const r = s.resolve
  s.open = false
  s.resolve = null
  r?.(true)
}
function cancel() {
  if (s.alertOnly) return okay()
  const r = s.resolve
  s.open = false
  s.resolve = null
  r?.(false)
}

// Autofocus confirm button (atau cancel kalau ada) saat dibuka.
watch(() => s.open, async (open) => {
  if (!open) return
  await nextTick()
  ;(s.alertOnly ? confirmBtn.value : confirmBtn.value)?.focus()
})

// Global ESC handler — dipasang manual karena dialog di-Teleport ke body dan
// @keydown.esc tidak fokus saat user belum klik apa-apa.
if (import.meta.client) {
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && s.open) cancel()
    if (e.key === 'Enter'  && s.open) okay()
  })
}
</script>
